import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import DropdownEditor from '../components/DropdownEditor';
import Formatters from '../components/grid-addons/formatters';
import componentQuery from '../utils/componentQuery';
import { default as Form } from '../components/connectedForm';
import Dropdown from '../components/connectedForm/Dropdown';
import NominalConfig from '../graphql/entities/nominal/NominalConfig';
import { IEditableDataGridColumnDef } from '../@types/editableDataGridTypes';
import { IDimension, INominalConfig, Maybe } from '../graphql/schema/p4types';
import { IDropdownProps } from '../@types/dropdown';
import { useNominalConfig } from './FetchConfig';

function getDimensionRowMetaData(rowData: any, column: any) {
  const options = column.options.dropdown?.optionList;
  return { options: options };
}

export function renderDimension(idx: number, dimension: Maybe<IDimension>) {
  if (!dimension) return null;

  const config = createDropdownOptions(dimension);

  return (
    <Form.Field<IDropdownProps>
      dataid={`dimensionValueEmbedded.dimensionReference${idx}`}
      component={Dropdown}
      label={dimension.title!}
      options={config.optionList}
      labelKey={config.labelKey}
      valueKey={config.valueKey}
    />
  );
}

export function createDropdownOptions(
  dimension: IDimension
): Partial<Omit<IDropdownProps, 'options'>> & { optionList: IDropdownProps['options'] } {
  const referenceList = dimension.dimensionReferenceList;
  const options: Array<any> = [];
  referenceList?.forEach((opt) => {
    const option = {
      id: opt!.id,
      concurrencyVersion: opt!.concurrencyVersion,
      description: opt!.description,
      dimension: {
        id: dimension.id,
        concurrencyVersion: dimension.concurrencyVersion,
      },
    };
    options.push(option);
  });
  return {
    optionList: options,
    valueKey: 'id',
    labelKey: 'description',
    remoteFilter: false,
    filterOptions: {
      matchPos: 'any',
      matchProp: 'label',
      ignoreCase: true,
    },
  };
}

function createDimension(dimension: IDimension, idx: number): IEditableDataGridColumnDef {
  const dimensionDatasource = createDropdownOptions(dimension);
  return {
    name: dimension.title,
    key: `dimensionValueEmbedded.dimensionReference${idx}`,
    editable: true,
    resizable: true,
    editor: <DropdownEditor />,
    formatter: Formatters.DropdownFormatter,
    options: { dropdown: dimensionDatasource },
    getRowMetaData: getDimensionRowMetaData as any,
  };
}

function addDimensionColumns(columnMeta: Array<IEditableDataGridColumnDef>, nominalConfig: INominalConfig) {
  let nominalColumn = columnMeta.findIndex((col) => col.name === 'Nominal');
  if (nominalColumn === -1) nominalColumn = 1;
  const { dimension1, dimension2, dimension3 } = nominalConfig;
  const columns = [...columnMeta];
  if (dimension1) {
    const dimColumn = createDimension(dimension1, 1);
    columns.splice(nominalColumn + 1, 0, dimColumn);
  }
  if (dimension2) {
    const dimColumn = createDimension(dimension2, 2);
    columns.splice(nominalColumn + 2, 0, dimColumn);
  }
  if (dimension3) {
    const dimColumn = createDimension(dimension3, 3);
    columns.splice(nominalColumn + 3, 0, dimColumn);
  }

  return columns;
}

function setDimensionConfig(dimension: IDimension, idx: number) {
  const options = createDropdownOptions(dimension);
  const { optionList, ...rest } = options;
  return {
    label: dimension.title,
    key: `dimensionReference${idx}`,
    options: optionList,
    ...rest,
  };
}

export function setDimensionInputs(nominalConfig: INominalConfig) {
  const { dimension1, dimension2, dimension3, useDimensionsOnBalanceSheet } = nominalConfig;
  const config: {
    useDimensionsOnBalanceSheet: Maybe<boolean>;
    dimension1: Maybe<ReturnType<typeof setDimensionConfig>>;
    dimension2: Maybe<ReturnType<typeof setDimensionConfig>>;
    dimension3: Maybe<ReturnType<typeof setDimensionConfig>>;
  } = { useDimensionsOnBalanceSheet, dimension1: null, dimension2: null, dimension3: null };
  if (dimension1) {
    config.dimension1 = setDimensionConfig(dimension1, 1);
  }
  if (dimension2) {
    config.dimension2 = setDimensionConfig(dimension2, 2);
  }
  if (dimension3) {
    config.dimension3 = setDimensionConfig(dimension3, 3);
  }
  return config;
}

export async function getNominalConfig() {
  const query = `query NominalConfig {
    nominalConfig {
      id
      concurrencyVersion
      useDimensionsOnBalanceSheet
      dimension1 {
        id
        concurrencyVersion
        title
        dimensionReferenceList {
          id
          concurrencyVersion
          description
        }
      }
      dimension2 {
        id
        concurrencyVersion
        title
        dimensionReferenceList {
          id
          concurrencyVersion
          description
        }
      }
      dimension3 {
        id
        concurrencyVersion
        title
        dimensionReferenceList {
          id
          concurrencyVersion
          description
        }
      }
    }
  }`;
  let nominalConfig = {};
  try {
    await componentQuery(query).then(({ response: data }: any) => {
      nominalConfig = data && data.nominalConfig ? data.nominalConfig : {};
    });
  } catch (e) {
    console.error('Nominal configuration could not be read', e);
  }
  return nominalConfig as INominalConfig;
}

export async function getDimensionColumns(columnMeta: Array<IEditableDataGridColumnDef>) {
  const nominalConfig = await getNominalConfig();
  return addDimensionColumns(columnMeta, nominalConfig);
}

export const useDimensionConfig = () => {
  const nominalConfig = useNominalConfig();
  return setDimensionInputs((nominalConfig ?? {}) as INominalConfig);
};

/**
 * Custom hook to add dimension columns.
 * @param columnMeta            Columns 'before' the dimension columns.
 * @param additionalColumnMeta  Columns 'after' the dimension columns.
 * @returns [...]               Array of columns with dimensions added once the query returns.
 */
export function useDimensionColumns(columnMeta: Array<IEditableDataGridColumnDef>, additionalColumnMeta?: Array<any>) {
  const [columns, setColumns] = useState(columnMeta);
  const { data } = useQuery(NominalConfig.dimensions);

  //Wrapped in useEffect rather than using query 'onCompleted' because we need to regen columns when 'additionalColumnMeta' changes as well as the query result.
  useEffect(() => {
    if (data?.nominalConfig) {
      const newColumns = addDimensionColumns(columnMeta, data.nominalConfig);
      newColumns.push(...(additionalColumnMeta || []));
      setColumns(newColumns);
    }
  }, [data, additionalColumnMeta, columnMeta]);

  return columns;
}
